import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../layout/header/Header';
import Footer from '../../layout/footer/Footer';
import { fetchHomeCategories, resolveCategoryImage } from '../../data/productsApi';
import './Home.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

const BRANDS = [
    { src: 'ce1dc7_1f758ba5c48646139c24eb6348f1cbf2-mv2.jpg', alt: 'Cartier' },
    { src: 'Rolex logo with black background.jpg', alt: 'Watch' },
    { src: 'ce1dc7_0b333e19c6fc44fb96c08e09d9e6533d-mv2.jpg', alt: 'CASIO' },
    { src: 'ce1dc7_302116be237e46c3885d8231e5e4daae-mv2.jpg', alt: 'TISSOT' },
    { src: 'ce1dc7_1a6bde89653342639240b8a71e09f9f2-mv2.jpg', alt: 'OMEGA' },
];

type HomeCategoryItem = {
    key: string;
    title: string;
    imageUrl: string;
};

const Home = () => {
    const navigate = useNavigate();
    const [homeCategories, setHomeCategories] = useState<HomeCategoryItem[]>([]);

    const handleExploreCategory = (title: string) => {
        if (title === 'All Products') {
            navigate('/products?category=all');
            return;
        }
        navigate(`/products?category=${encodeURIComponent(title)}`);
    };

    useEffect(() => {
        let mounted = true;

        const loadHomeCategories = async () => {
            try {
                const categories = await fetchHomeCategories();
                if (!mounted) return;

                const mapped = categories
                    .filter((category) => typeof category.imageUrl === 'string' && category.imageUrl.startsWith('/uploads/categories/'))
                    .map((category) => ({
                        key: category._id,
                        title: category.name,
                        imageUrl: resolveCategoryImage(category.imageUrl),
                    }));

                setHomeCategories(mapped);
            } catch {
                if (!mounted) return;
                setHomeCategories([]);
            }
        };

        loadHomeCategories();

        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('revealed'); }),
            { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
        );
        document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
        return () => {
            mounted = false;
            observer.disconnect();
        };
    }, []);

    const categoryItems = useMemo(() => {
        const allProductsItem: HomeCategoryItem = {
            key: 'all-products',
            title: 'All Products',
            imageUrl: `${PUBLIC_URL}/arkab/allproducts.jpg`,
        };

        return [allProductsItem, ...homeCategories];
    }, [homeCategories]);

    return (
        <div className="arkab-home">
            <Header />

            {/* ── HERO ── */}
            <section
                className="arkab-hero"
                style={{ backgroundImage: `url(${PUBLIC_URL}/arkab/hero-image.jpg)` }}
            >
                <div className="hero-overlay" />
                <div className="hero-content">
                    <h1 className="hero-title">
                        Incredible Prices<br />
                        On All Yours<br />
                        Favorite Items
                    </h1>
                </div>
            </section>

            {/* ── SHOP BY CATEGORY ── */}
            <section className="arkab-categories reveal">
                <h2 className="section-heading">Shop By Category</h2>
                <div className="cat-circle-grid">
                    {categoryItems.map((item) => (
                        <article key={item.key} className="cat-circle-card">
                            <img src={item.imageUrl} alt={item.title} className="cat-circle-image" />
                            <h3>{item.title}</h3>
                            <button className="cat-explore-btn" onClick={() => handleExploreCategory(item.title)}>Explore</button>
                        </article>
                    ))}
                </div>
            </section>

            {/* ── BENEFITS STRIP ── */}
            <section className="arkab-benefits reveal">
                <div className="benefits-grid">
                    <div className="benefit-item">
                        <span className="benefit-icon">🚗</span>
                        <span className="benefit-text">Curb-side pickup</span>
                    </div>
                    <div className="benefit-item">
                        <span className="benefit-icon">📦</span>
                        <span className="benefit-text">Free shipping on orders over $50</span>
                    </div>
                    <div className="benefit-item">
                        <span className="benefit-icon">💰</span>
                        <span className="benefit-text">Low prices guaranteed</span>
                    </div>
                    <div className="benefit-item">
                        <span className="benefit-icon">🕐</span>
                        <span className="benefit-text">Available to you 24/7</span>
                    </div>
                </div>
            </section>

            {/* ── BRANDS ── */}
            <section className="arkab-brands reveal">
                <h2 className="section-heading">Brands</h2>
                <div className="brands-strip">
                    {BRANDS.map(b => (
                        <div key={b.alt} className="brand-item">
                            <img src={`${PUBLIC_URL}/arkab/${b.src}`} alt={b.alt} />
                        </div>
                    ))}
                </div>
            </section>

            {/* ── HELP CENTER CTA ── */}
            <section className="arkab-help-cta reveal">
                <div className="help-text">
                    <h2>Need Help? Check Out Our Help Center</h2>
                    <p>I'm a paragraph. Click here to add your own text and edit me. Let your users get to know you.</p>
                    <button className="help-btn" onClick={() => navigate('/contact')}>Go to Help Center</button>
                </div>
                <div className="help-image">
                    <img
                        src={`${PUBLIC_URL}/arkab/ce1dc7_ec9cc0d6f3a94da7be42bca26d75618a-mv2.jpg`}
                        alt="Premium Watch"
                    />
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Home;
